/**
 * Plain-language text for the commercial history and the billed-money warning.
 *
 * Kept as pure functions so the wording is tested once and the dialog and the
 * history lines only lay it out.
 */

import {
  commercialFieldLabel,
  type CommercialChange,
  type ReportBillingStatus,
} from '@/types/commercial-change';
import { formatDueDate } from './businessDays';

const DATE_FIELDS = new Set(['due_date', 'np_due_date']);
const FLAG_FIELDS = new Set([
  'is_proof_of_concept',
  'is_research_and_development',
  'is_excluded_from_billing',
]);
const DAY_FIELDS = new Set(['turnaround_time_days', 'np_turnaround_time_days']);

/** A recorded value as a person would read it ("12 Oct 2026", "Yes", "5 days"). */
export function formatCommercialValue(
  field: string,
  value: string | null,
  nameFor?: (userId: string) => string
): string {
  if (value === null || value === '') {
    return '—';
  }
  if (FLAG_FIELDS.has(field)) {
    return value === 'true' ? 'Yes' : 'No';
  }
  if (DATE_FIELDS.has(field)) {
    // A bare date is the whole day; keep it on that day whatever the viewer's zone.
    return formatDueDate(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00Z` : value);
  }
  if (DAY_FIELDS.has(field)) {
    return `${value} ${value === '1' ? 'day' : 'days'}`;
  }
  if (field === 'jurisdictions') {
    return value.split(',').join(', ');
  }
  if (field === 'cx_lead') {
    return nameFor ? nameFor(value) : value;
  }
  return value;
}

/** "Price: 100 → 120" — one history row's change. */
export function describeChange(
  change: Pick<CommercialChange, 'field' | 'oldValue' | 'newValue'>,
  nameFor?: (userId: string) => string
): string {
  const from = formatCommercialValue(change.field, change.oldValue, nameFor);
  const to = formatCommercialValue(change.field, change.newValue, nameFor);
  return `${commercialFieldLabel(change.field)}: ${from} → ${to}`;
}

/** "Last changed by Alice on 12 Oct 2026" — the line beside a value. */
export function describeLastChange(
  change: Pick<CommercialChange, 'actorUserId' | 'createdAt'>,
  nameFor?: (userId: string) => string
): string {
  const who = change.actorUserId
    ? (nameFor?.(change.actorUserId) ?? change.actorUserId)
    : 'the system';
  const when = change.createdAt ? ` on ${formatDueDate(change.createdAt)}` : '';
  return `Last changed by ${who}${when}`;
}

/** "changing the price and due date" — the fields an edit touches, in words. */
export function describeFields(fields: readonly string[]): string {
  const labels = [
    ...new Set(
      fields.map((field) =>
        FLAG_FIELDS.has(field) ? 'billing flags' : commercialFieldLabel(field).toLowerCase()
      )
    ),
  ];
  if (labels.length <= 1) {
    return labels[0] ?? 'figures';
  }
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

function monthYear(month: number, year: number): string {
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleDateString('en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export interface BilledWarning {
  /** `warn`: the money may have gone out. `caution`: we could not check.
   * `info`: nothing to worry about. */
  tone: 'warn' | 'caution' | 'info';
  message: string;
}

/**
 * What to tell someone about to change a completed report's figures.
 * `assignmentId` focuses it on one partner's fee; leave it out for the
 * client-side figures. Returns null while the answer is still loading.
 */
export function describeBilledWarning(
  status: ReportBillingStatus | null,
  options: { assignmentId?: string | null; loading?: boolean } = {}
): BilledWarning | null {
  if (options.loading) {
    return null;
  }
  if (!status) {
    return {
      tone: 'caution',
      message: 'Billing could not be checked, so assume this money may already have gone out.',
    };
  }
  if (status.source === 'no_project') {
    return {
      tone: 'info',
      message: 'There is no Backoffice project yet, so nothing has been billed.',
    };
  }

  if (status.source === 'estimate') {
    const completed = status.completedAt
      ? monthYear(
          new Date(status.completedAt).getUTCMonth() + 1,
          new Date(status.completedAt).getUTCFullYear()
        )
      : null;
    if (status.mayHaveBeenBilled) {
      return {
        tone: 'warn',
        message: `Billing could not be checked. This report completed in ${completed ?? 'an earlier month'}, so that month may already have been billed.`,
      };
    }
    return {
      tone: 'caution',
      message:
        'Billing could not be checked. This report completed this month, so it has probably not been billed yet.',
    };
  }

  if (options.assignmentId) {
    const billed = status.assignmentsBilled.find((a) => a.assignmentId === options.assignmentId);
    if (billed) {
      return {
        tone: 'warn',
        message: `This partner’s fee was billed in ${monthYear(billed.billingMonth, billed.billingYear)}. Changing it now may not change what was paid.`,
      };
    }
    return { tone: 'info', message: 'This partner’s fee has not been billed yet.' };
  }

  if (status.clientBilled) {
    const when =
      status.clientBillingMonth && status.clientBillingYear
        ? ` in ${monthYear(status.clientBillingMonth, status.clientBillingYear)}`
        : '';
    return {
      tone: 'warn',
      message: `This report was billed${when}. Changing its figures now may not change what was invoiced.`,
    };
  }
  return { tone: 'info', message: 'Billing records show nothing billed for this report yet.' };
}
